import type { AbilityDef } from "./ability";
import {
  type AttackButton,
  ATTACK_BUTTONS,
  BUTTON,
  DIRECTION,
  relativeDirection,
  type DirectionName,
  type InputBits,
} from "./input";
import { HISTORY, PRESS_NEVER, TAPS, type FighterState } from "./state";

/** Курки суперприёма «вместе» — нажаты с разницей не больше стольких кадров (консультант 04.11.2026: 2–3 кадра). */
export const SUPER_WINDOW = 3;
/** Сколько кадров нажатие ждёт, пока герой освободится (буфер ввода). */
export const PRESS_BUFFER = 5;
/** За сколько кадров нужно ввести «назад, вперёд». */
export const MOTION_WINDOW = 14;

const FORWARDS: readonly number[] = [DIRECTION.F, DIRECTION.DF, DIRECTION.UF];
const BACKS: readonly number[] = [DIRECTION.B, DIRECTION.DB, DIRECTION.UB];
const DOWNS: readonly number[] = [DIRECTION.D, DIRECTION.DF, DIRECTION.DB];
const UPS: readonly number[] = [DIRECTION.U, DIRECTION.UF, DIRECTION.UB];

/** Совпадает ли фактическое направление с нужным: диагональ засчитывается за обе стороны. */
export function directionMatches(actual: number, wanted: DirectionName): boolean {
  switch (wanted) {
    case "F":
      return FORWARDS.includes(actual);
    case "B":
      return BACKS.includes(actual);
    case "D":
      return DOWNS.includes(actual);
    case "U":
      return UPS.includes(actual);
    default:
      return actual === DIRECTION[wanted];
  }
}

/** Запомнить нажатия кадра: направление от лица героя и «возраст» нажатий кнопок. */
export function recordInput(fighter: FighterState, bits: InputBits): void {
  const previous = fighter.history[fighter.historyHead] ?? 0;
  fighter.historyHead = (fighter.historyHead + 1) % HISTORY;
  fighter.history[fighter.historyHead] = relativeDirection(bits, fighter.facing);
  recordTaps(fighter, fighter.history[fighter.historyHead] ?? 0, previous);
  ATTACK_BUTTONS.forEach((name, index) => {
    const mask = BUTTON[name];
    const pressedNow = (bits & mask) !== 0 && (fighter.lastBits & mask) === 0;
    const age = fighter.pressAge[index] ?? PRESS_NEVER;
    fighter.pressAge[index] = pressedNow ? 0 : Math.min(PRESS_NEVER, age + 1);
    const held = fighter.held[index] ?? 0;
    fighter.held[index] = (bits & mask) !== 0 ? Math.min(HOLD_MAX, Math.max(0, held) + 1) : held > 0 ? -held : 0;
  });
  // суперприём — оба курка в одном окне: R2 — ещё и блок, держать блок и дожать L2 позже — не суперприём
  fighter.quiet = bits === 0 ? Math.min(999, fighter.quiet + 1) : 0;
  [BUTTON.super, BUTTON.enhance, BUTTON.taunt, BUTTON.grab].forEach((mask, index) => {
    const pressedNow = (bits & mask) !== 0 && (fighter.lastBits & mask) === 0;
    fighter.triggerAge[index] = pressedNow ? 0 : Math.min(PRESS_NEVER, (fighter.triggerAge[index] ?? PRESS_NEVER) + 1);
  });
  const combo = BUTTON.super | BUTTON.enhance;
  const together = (bits & combo) === combo;
  const before = (fighter.lastBits & combo) === combo;
  const inWindow = Math.max(fighter.triggerAge[0] ?? PRESS_NEVER, fighter.triggerAge[1] ?? PRESS_NEVER) <= SUPER_WINDOW;
  fighter.superAge = together && !before && inWindow ? 0 : Math.min(PRESS_NEVER, fighter.superAge + 1);
  fighter.lastBits = bits;
}

export const currentDirection = (fighter: FighterState): number => fighter.history[fighter.historyHead] ?? 0;

/** Коды касаний направлений в FighterState.tapCode. */
export const TAP_CODE: Readonly<Record<"F" | "B" | "D" | "U", number>> = { F: 1, B: 2, D: 3, U: 4 };
/**
 * Жест — по смене направления, а не по длине касания (консультант 04.11.2026: стик держат дольше и отпускают не в ноль):
 * вход в направление, выход из него, вход в следующее; от входа до следующего входа — не дольше GESTURE_STEP кадров,
 * последнее касание ждёт GESTURE_BUFFER кадров, пока герой занят. Шаг назад дольше GESTURE_STEP — ходьба, не жест.
 */
export const GESTURE_STEP = 24;
export const GESTURE_BUFFER = 4;

const horizontalTap = (direction: number): number =>
  directionMatches(direction, "F") ? TAP_CODE.F : directionMatches(direction, "B") ? TAP_CODE.B : 0;
const verticalTap = (direction: number): number =>
  directionMatches(direction, "D") ? TAP_CODE.D : directionMatches(direction, "U") ? TAP_CODE.U : 0;

/** Касания направлений кадра: стареют, держатся или отпущены; новое — когда составляющая только что появилась. */
function recordTaps(fighter: FighterState, direction: number, previous: number): void {
  const horizontal = horizontalTap(direction);
  const vertical = verticalTap(direction);
  for (let index = 0; index < TAPS; index++) {
    fighter.tapAge[index] = Math.min(PRESS_NEVER, (fighter.tapAge[index] ?? PRESS_NEVER) + 1);
    const held = fighter.tapHeld[index] ?? 0;
    const code = fighter.tapCode[index] ?? 0;
    if (held > 0)
      fighter.tapHeld[index] = code === horizontal || code === vertical ? Math.min(PRESS_NEVER, held + 1) : -held;
  }
  for (const [code, before] of [
    [vertical, verticalTap(previous)],
    [horizontal, horizontalTap(previous)],
  ] as const) {
    if (code === 0 || code === before) continue;
    for (let index = TAPS - 1; index > 0; index--) {
      fighter.tapCode[index] = fighter.tapCode[index - 1] ?? 0;
      fighter.tapAge[index] = fighter.tapAge[index - 1] ?? PRESS_NEVER;
      fighter.tapHeld[index] = fighter.tapHeld[index - 1] ?? 0;
    }
    fighter.tapCode[0] = code;
    fighter.tapAge[0] = 0;
    fighter.tapHeld[0] = 1;
  }
}

/** Жест сделан: последние касания — его направления по порядку, прежние — короткие, всё уложилось в окна. */
export function gestureDone(fighter: FighterState, gesture: readonly DirectionName[]): boolean {
  const count = gesture.length;
  if (count === 0 || count > TAPS || (fighter.tapAge[0] ?? PRESS_NEVER) > GESTURE_BUFFER) return false;
  for (let index = 0; index < count; index++) {
    const wanted = gesture[count - 1 - index];
    const code = wanted === "F" || wanted === "B" || wanted === "D" || wanted === "U" ? TAP_CODE[wanted] : -1;
    if ((fighter.tapCode[index] ?? 0) !== code) return false;
    if (index === 0) continue;
    const gap = (fighter.tapAge[index] ?? PRESS_NEVER) - (fighter.tapAge[index - 1] ?? PRESS_NEVER);
    if (gap > GESTURE_STEP) return false;
  }
  return true;
}

/** Жест сработал — касания забыты, чтобы он не повторился. */
const forgetTaps = (fighter: FighterState): void => {
  fighter.tapCode.fill(0);
};

/**
 * Жест сделан, а Ярости на приём нет: id приёма (касания забыты) или undefined. Игроку видно, что жест узнан, — иначе
 * «три раза назад не делает невидимкой» без объяснения (Джон 04.11.2026, 23:22).
 */
export function gestureWithoutMeter(
  fighter: FighterState,
  kit: readonly AbilityDef[],
  airborne: boolean,
): string | undefined {
  const ability = kit.find(
    (candidate) =>
      candidate.input.gesture !== undefined &&
      (candidate.input.air ?? false) === airborne &&
      (candidate.cost ?? 0) > fighter.meter &&
      gestureDone(fighter, candidate.input.gesture),
  );
  if (ability) forgetTaps(fighter);
  return ability?.id;
}

function motionDone(fighter: FighterState, motion: readonly DirectionName[]): boolean {
  let step = motion.length - 1;
  for (let age = 0; age < MOTION_WINDOW && step >= 0; age++) {
    const actual = fighter.history[(fighter.historyHead - age + HISTORY) % HISTORY] ?? 0;
    const wanted = motion[step];
    if (wanted !== undefined && directionMatches(actual, wanted)) step--;
  }
  return step < 0;
}

/** Порядок проверки: сначала длинные вводы, потом «держать + кнопка», потом просто кнопка. */
const priorityCache = new WeakMap<readonly AbilityDef[], number[]>();
function priorityOrder(kit: readonly AbilityDef[]): number[] {
  let order = priorityCache.get(kit);
  if (!order) {
    const score = (ability: AbilityDef): number =>
      (ability.input.gesture ? 25 + ability.input.gesture.length * 3 : 0) +
      (ability.input.near !== undefined ? 6 : 0) +
      (ability.input.with ? 20 : 0) +
      (ability.input.charge !== undefined ? 15 : 0) +
      (ability.input.motion?.length ?? 0) * 10 +
      (ability.input.run ? 8 : 0) +
      (ability.input.hold ? 5 : 0);
    order = kit.map((_, index) => index).sort((a, b) => score(kit[b] as AbilityDef) - score(kit[a] as AbilityDef));
    priorityCache.set(kit, order);
  }
  return order;
}

/** Дольше удержание не считаем: заряд уже полный. */
export const HOLD_MAX = 120;

/** Две кнопки «вместе» — нажаты с разницей не больше стольких кадров. */
const PAIR_WINDOW = 3;

/** Кнопка уже начатого удара: она «нажата» столько кадров назад — для замены удара приёмом на две кнопки. */
export interface UsedPress {
  readonly button: AttackButton;
  readonly age: number;
}

/**
 * Какую способность начать сейчас: индекс в наборе или -1. Нажатие при этом «съедается». used — только приёмы на
 * две кнопки: вторая кнопка пришла кадром позже первой, и удар по первой (used) ещё можно заменить.
 */
export function pickAbility(
  fighter: FighterState,
  kit: readonly AbilityDef[],
  airborne: boolean,
  used?: UsedPress,
  distance = Infinity,
  buffer = PRESS_BUFFER,
): number {
  const ageOf = (button: AttackButton): number =>
    used && used.button === button ? used.age : (fighter.pressAge[ATTACK_BUTTONS.indexOf(button)] ?? PRESS_NEVER);
  for (const index of priorityOrder(kit)) {
    const ability = kit[index];
    if (!ability) continue;
    const { input } = ability;
    if (input.series || input.taunt || input.grab || (used && !input.with)) continue;
    if (input.near !== undefined && distance > input.near) continue;
    if (input.gesture) {
      // жест направлениями: кнопки нет, только касания по очереди
      if (used || (input.air ?? false) !== airborne || !gestureDone(fighter, input.gesture)) continue;
      if ((ability.cost ?? 0) > fighter.meter) continue;
      if (!ability.keepTaps) forgetTaps(fighter);
      return index;
    }
    if (!input.button) continue;
    const button = input.button;
    if (input.charge !== undefined) {
      // заряд: кнопку продержали не меньше charge кадров и отпустили в этот кадр
      if (used || (input.air ?? false) !== airborne) continue;
      const released = -(fighter.held[ATTACK_BUTTONS.indexOf(button)] ?? 0);
      if (released < input.charge || (ability.cost ?? 0) > fighter.meter) continue;
      return index;
    }
    if ((input.air ?? false) !== airborne) continue;
    if (input.run && !fighter.running) continue;
    const buttonIndex = ATTACK_BUTTONS.indexOf(button);
    const age = ageOf(button);
    if (age > buffer) continue;
    if (input.with) {
      const pairIndex = ATTACK_BUTTONS.indexOf(input.with);
      const pairAge = ageOf(input.with);
      if (pairAge > buffer || Math.abs(pairAge - age) > PAIR_WINDOW) continue;
      if ((ability.cost ?? 0) > fighter.meter) continue;
      fighter.pressAge[pairIndex] = PRESS_NEVER;
      fighter.pressAge[buttonIndex] = PRESS_NEVER;
      return index;
    }
    if (input.hold && !directionMatches(currentDirection(fighter), input.hold)) continue;
    if (!input.hold && !input.motion && DOWNS.includes(currentDirection(fighter)) && !airborne) continue;
    if (input.motion && !motionDone(fighter, input.motion)) continue;
    if ((ability.cost ?? 0) > fighter.meter) continue;
    fighter.pressAge[buttonIndex] = PRESS_NEVER;
    return index;
  }
  return -1;
}
